import React, { useState, useCallback, useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import SearchFilterDropdown from "components/Common/SearchableDropdown/SearchFilterDropdown";

interface GoogleLocationWithDropdownProps {
  onSelect?: (selectedOption: google.maps.places.AutocompletePrediction | null) => void;
  placeholder?: string;
  selectedLocations: string | null;
  allLocations?: []; // Array of objects with city and radius
  handleChange?: (values: string[] | string) => void | undefined;
  disabled?: boolean;
  isBorder?: boolean;
  isSmall?: boolean;
  style?: any;
}

const libraries: Array<"places"> = ["places"]; // Load the places library

const GoogleLocationWithDropdown: React.FC<GoogleLocationWithDropdownProps> = ({
  placeholder,
  selectedLocations,
  handleChange,
  allLocations = [],
  disabled = false,
  isBorder = true,
  isSmall = false,
  style={}
}) => {
  const [options, setOptions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "",
    libraries,
  });

  const fetchLocations = useCallback(
    (inputValue: string) => {
      if (!isLoaded) return;
      const service = new google.maps.places.AutocompleteService();
      service.getPlacePredictions({ input: inputValue }, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setOptions(predictions);
        } else {
          setOptions([]);
        }
        setLoading(false);
      });
    },
    [isLoaded]
  );

  const handleInputChange = (inputValue: string) => {
    if (inputValue.length > 2) {
      setLoading(true);
      fetchLocations(inputValue);
    }
  };

  // Normalize allLocations for easier comparison
  const existingCities = allLocations.map((location: any) =>
    location ? location.toLowerCase() : ""
  );

  // Filter options against allLocations
  const filterOptions = () => {
    const filtered = options.filter((option) => {
      const optionCity = option.description.toLowerCase();
      return !existingCities.includes(optionCity);
    });
    setFilteredOptions(filtered);
  };

  useEffect(() => {
    filterOptions();
  }, [options]);

  return (
    <SearchFilterDropdown
      options={filteredOptions.map((location) => ({
        value: location.description,
        label: location.description,
      }))}
      onChange={(values: string[] | string) => {
        if (handleChange) {
          handleChange(values);
        }
      }}
      isBorder={isBorder}
      onInputChange={handleInputChange}
      placeholder={placeholder}
      defaultValue={selectedLocations}
      loading={loading}
      disabled={disabled}
      isSmall={isSmall}
      style={style}
    />
  );
};

export default GoogleLocationWithDropdown;
